import { nextTick, onMounted, type Ref } from 'vue'

/**
 * Autofocus op het eerste veld van een nieuw formulier (vraag klant 29/09/2026):
 * wie "Contactpersoon aanmaken" opent, moet meteen de naam kunnen typen.
 *
 * Regels:
 * - "Eerste veld" = het eerste zichtbare, bewerkbare TEKSTVELD (input of textarea).
 *   Een native <select> slaan we bewust over: die heeft altijd een waarde (dus nooit
 *   "leeg") en focus erop laat niemand meteen typen. Staat er een select bovenaan, dan
 *   krijgt het tekstveld eronder de focus. (Docs rechtgezet in de GH #722 review: er
 *   stond "eerste veld", terwijl de code selects al oversloeg.)
 * - Enkel als dat tekstveld leeg is. Bij bewerken staat er al een waarde en focussen
 *   we niets, zodat je niet per ongeluk iets overschrijft.
 * - Niet op touchtoestellen: daar schuift anders ongevraagd het toetsenbord open
 *   en verspringt de modal.
 * - Niet als de gebruiker (of het component zelf, bv. de zoekmodal) al een veld
 *   binnen het formulier gefocust heeft.
 * - Geen datumveld: focus opent daar de kalender.
 *
 * Componenten die bij focus iets openen of doen (lijst, dropdown, tooltip, alles
 * selecteren) moeten SILENT_FOCUS_ATTR respecteren via isSilentFocus(): bij autofocus
 * blijft alles dicht tot de gebruiker zelf klikt of typt. VModal zet autofocus sinds
 * GH #722 standaard aan voor alle modals; zonder die check opende bv. de e-mailmodal
 * met de adressen-dropdown over het onderwerpveld.
 */
export const SILENT_FOCUS_ATTR = 'data-silent-focus'

/** Komt deze focus van de autofocus hieronder (en niet van de gebruiker)? */
export function isSilentFocus(event: Event | null | undefined): boolean {
    const target = event?.target
    return target instanceof Element && target.hasAttribute(SILENT_FOCUS_ATTR)
}

// Geen `select`: zie "Eerste veld" hierboven.
const FIELD_SELECTOR = [
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([type="reset"])',
    'textarea',
].join(', ')

type Field = HTMLInputElement | HTMLTextAreaElement

function isVisible(element: HTMLElement): boolean {
    return element.getClientRects().length > 0
}

function isTouchDevice(): boolean {
    return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
}

export function focusFirstEmptyField(root: HTMLElement | null | undefined): boolean {
    if (!root || isTouchDevice()) {
        return false
    }

    const active = document.activeElement
    if (active && active !== document.body && root.contains(active)) {
        return false
    }

    const field = Array.from(root.querySelectorAll<Field>(FIELD_SELECTOR)).find(
        (element) => !element.disabled && !element.readOnly && isVisible(element)
    )

    if (!field || field.closest('.dp__main') || field.value.trim() !== '') {
        return false
    }

    // focus() vuurt focus/focusin synchroon af, dus het attribuut is zichtbaar voor de
    // handlers (isSilentFocus) en meteen daarna weer weg.
    field.setAttribute(SILENT_FOCUS_ATTR, '')
    field.focus({ preventScroll: true })
    field.removeAttribute(SILENT_FOCUS_ATTR)

    return document.activeElement === field
}

/** Wacht tot de DOM (en een eventuele enter-transitie) klaar is en focust dan. */
export function autofocusFirstFieldSoon(getRoot: () => HTMLElement | null | undefined): void {
    void nextTick(() => {
        requestAnimationFrame(() => {
            focusFirstEmptyField(getRoot())
        })
    })
}

/** Voor volle pagina's met een formulier: focus bij het openen van de pagina. */
export function useAutofocusFirstField(root: Ref<HTMLElement | null>): void {
    onMounted(() => autofocusFirstFieldSoon(() => root.value))
}
